import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { API_URL } from "../../config";
import Swal from "sweetalert2";
import { BarLoader, ClipLoader } from "react-spinners";

const TeacherReg = () => {
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [surname, setSurname] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [collegeName, setCollegeName] = useState('');
    const [collegeDescription, setCollegeDescription] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [otp, setOtp] = useState('');
    const [vOtp, setVOtp] = useState('');
    const [originalOtp, setOriginalOtp] = useState(''); // Store original OTP for verification
    const [otpSent, setOtpSent] = useState(false); // Track if OTP has been sent
    const [collegeLogo, setCollegeLogo] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [formData2, setFormData2] = useState({
        username: '',
        user_email: '',
        otp: ''
    });

    // Improved encryption function with TextEncoder/TextDecoder
    const encryptData = (data) => {
        try {
            const key = "BlueOceanElearning2026!@#";
            const jsonString = JSON.stringify(data);
            
            // Convert string to UTF-8 array for better handling
            const encoder = new TextEncoder();
            const dataBytes = encoder.encode(jsonString);
            const keyBytes = encoder.encode(key);
            
            // XOR encryption
            const encryptedBytes = new Uint8Array(dataBytes.length);
            for (let i = 0; i < dataBytes.length; i++) {
                encryptedBytes[i] = dataBytes[i] ^ keyBytes[i % keyBytes.length];
            }
            
            // Convert to base64 for safe storage
            return btoa(String.fromCharCode.apply(null, encryptedBytes));
        } catch (error) {
            console.error('Encryption error:', error);
            return null;
        }
    };

    // Improved decryption function
    const decryptData = (encryptedData) => {
        try {
            if (!encryptedData) return null;
            
            const key = "BlueOceanElearning2026!@#";
            
            // Decode from base64
            const encryptedString = atob(encryptedData);
            const encryptedBytes = new Uint8Array(encryptedString.length);
            for (let i = 0; i < encryptedString.length; i++) {
                encryptedBytes[i] = encryptedString.charCodeAt(i);
            }
            
            const encoder = new TextEncoder();
            const keyBytes = encoder.encode(key);
            
            // XOR decryption
            const decryptedBytes = new Uint8Array(encryptedBytes.length);
            for (let i = 0; i < encryptedBytes.length; i++) {
                decryptedBytes[i] = encryptedBytes[i] ^ keyBytes[i % keyBytes.length];
            }
            
            // Convert back to string
            const decoder = new TextDecoder();
            const decryptedString = decoder.decode(decryptedBytes);
            
            return JSON.parse(decryptedString);
        } catch (error) {
            console.error('Decryption error:', error);
            return null;
        }
    };

    // Check if saved state is expired
    const isStateExpired = (savedState) => {
        if (!savedState || !savedState.timestamp) return true;
        const now = Date.now();
        const expiryTime = 30 * 60 * 1000; // 30 minutes in milliseconds
        return (now - savedState.timestamp) > expiryTime;
    };

    // Save state to localStorage with encryption and timestamp
    const saveStateToLocalStorage = (state) => {
        try {
            const stateWithTimestamp = {
                ...state,
                timestamp: Date.now()
            };
            const encryptedState = encryptData(stateWithTimestamp);
            if (encryptedState) {
                localStorage.setItem('teacherRegState', encryptedState);
                console.log('TeacherReg state saved successfully');
            }
        } catch (error) {
            console.error('Error saving state:', error);
        }
    };

    // Load and decrypt state from localStorage
    const loadStateFromLocalStorage = () => {
        try {
            const encryptedState = localStorage.getItem('teacherRegState');
            if (!encryptedState) return null;
            
            const decryptedState = decryptData(encryptedState);
            if (!decryptedState) {
                console.log('Failed to decrypt teacherReg state');
                localStorage.removeItem('teacherRegState');
                return null;
            }
            
            // Check if state is expired
            if (isStateExpired(decryptedState)) {
                console.log('TeacherReg state expired');
                localStorage.removeItem('teacherRegState');
                return null;
            }
            
            console.log('TeacherReg state loaded successfully');
            return decryptedState;
        } catch (error) {
            console.error('Error loading state:', error);
            localStorage.removeItem('teacherRegState');
            return null;
        }
    };

    // Load saved state from localStorage on component mount
    useEffect(() => {
        const savedState = loadStateFromLocalStorage();
        if (savedState) {
            console.log('Restoring saved teacher registration state');
            setName(savedState.name || '');
            setSurname(savedState.surname || '');
            setEmail(savedState.email || '');
            setPhone(savedState.phone || '');
            setCollegeName(savedState.collegeName || '');
            setCollegeDescription(savedState.collegeDescription || '');
            setPassword(savedState.password || '');
            setConfirmPassword(savedState.confirmPassword || '');
            setOtp(savedState.otp || '');
            setOriginalOtp(savedState.originalOtp || savedState.otp || '');
            setShowAddModal(savedState.showAddModal || false);
            setOtpSent(savedState.otpSent || false);
            setFormData2({
                username: savedState.name || '',
                user_email: savedState.email || '',
                otp: savedState.otp || ''
            });
            
            // Show warning if session is about to expire (within last 5 minutes)
            const timeRemaining = (savedState.timestamp + (30 * 60 * 1000)) - Date.now();
            if (timeRemaining > 0 && timeRemaining < 5 * 60 * 1000) {
                setTimeout(() => {
                    Swal.fire({
                        text: "Your registration session will expire in 5 minutes. Please complete the process soon.",
                        icon: "warning",
                        timer: 5000,
                        showConfirmButton: true
                    });
                }, 1000);
            }
        }
    }, []);

    // Save state to localStorage whenever relevant state changes
    useEffect(() => {
        if (showAddModal || (name && email)) {
            const stateToSave = {
                name,
                surname,
                email,
                phone,
                collegeName,
                collegeDescription,
                password,
                confirmPassword,
                otp,
                originalOtp,
                showAddModal,
                otpSent
            };
            saveStateToLocalStorage(stateToSave);
        }
    }, [name, surname, email, phone, collegeName, collegeDescription, password, confirmPassword, otp, originalOtp, showAddModal, otpSent]);

    const handleRegister = async () => {
        // Check if passwords match
        if (password !== confirmPassword) {
            Swal.fire({
                text: "Passwords do not match!",
                icon: "error"
            });
            return;
        }
        
        // Check if college logo is selected
        if (!collegeLogo) {
            Swal.fire({
                text: "Please upload a college logo!",
                icon: "error"
            });
            return;
        }
        
        setIsLoading(true);

        const formData = new FormData();
        formData.append('name', name);
        formData.append('surname', surname);
        formData.append('email', email);
        formData.append('phone', phone);
        formData.append('password', password);
        formData.append('collegeName', collegeName);
        formData.append('collegeDescription', collegeDescription);
        formData.append('file', collegeLogo);

        try {
            const response = await fetch(`${API_URL}/onboarding/teacher`, {
                method: "POST",
                body: formData
            });

            if (!response.ok) {
                throw new Error('Failed to register teacher');
            }

            const data = await response.json();
            console.log(data.status)
            if (data.status === '200') {
                Swal.fire({
                    text: "Successfully registered! You can proceed to login",
                    icon: "success"
                });
                setIsLoading(false);
                // Clear registration state
                localStorage.removeItem('teacherRegState');
                navigate('/');
            } else if (data.status === '401') {
                Swal.fire({
                    text: "The college name is already taken. Your college name should be unique!",
                    icon: "error"
                });
                setIsLoading(false);
            } else {
                Swal.fire({
                    text: "This email is already registered with another college!",
                    icon: "error"
                });
                setIsLoading(false);
            }

        } catch (err) {
            Swal.fire({
                text: "Registration failed, check your network connection!",
                icon: "error"
            });
            setIsLoading(false);
        }
    };

    const generateOtp = () => {
        // Only generate OTP if we don't already have one
        if (originalOtp && otpSent) {
            console.log('Using existing OTP for teacher registration:');
            return;
        }
        
        const min = 123456;
        const max = 987987;
        const random = min + (Math.floor(Math.random() * (max - min + 1)));
        const otpString = random.toString();
        console.log('Generated NEW OTP for teacher registration:', otpString);
        
        setOtp(otpString);
        setOriginalOtp(otpString);
        setFormData2({ ...formData2, otp: random });
    };

    const validateOtp = (e) => {
        e.preventDefault();
        console.log('=== Teacher Registration OTP Validation ===');
        // console.log('Entered OTP:', vOtp);
        // console.log('Original OTP (stored):', originalOtp);
        
        // Convert both to strings and trim
        const enteredOtpString = String(vOtp).trim();
        const expectedOtpString = String(originalOtp).trim();
        
        // console.log('Entered OTP (string):', enteredOtpString);
        // console.log('Expected OTP (string):', expectedOtpString);
        // console.log('OTP match:', enteredOtpString === expectedOtpString);
        
        if (enteredOtpString === expectedOtpString) {
            console.log('OTP verified successfully!');
            handleRegister();
        } else {
            console.log('OTP mismatch!');
            Swal.fire({
                text: `Incorrect OTP! Please check your email and try again.`,
                icon: "error"
            });
        }
        console.log('=====================================');
    };

    const sendOtp = async (e) => {
        e.preventDefault();
        
        // Check if passwords match before sending OTP
        if (password !== confirmPassword) {
            Swal.fire({
                text: "Passwords do not match!",
                icon: "error"
            });
            return;
        }
        
        // Check if college logo is selected
        if (!collegeLogo) {
            Swal.fire({
                text: "Please upload a college logo!",
                icon: "error"
            });
            return;
        }
        
        if (!email) {
            Swal.fire({
                text: "Please enter your email address!",
                icon: "error"
            });
            return;
        }
        
        setIsLoading(true);
        
        // Generate OTP if we don't have one
        if (!originalOtp) {
            generateOtp();
            // Wait for OTP to be generated
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        const otpToSend = originalOtp;
        // console.log('Sending OTP to email:', email);
        // console.log('OTP being sent:', otpToSend);
        
        const otpData = {
            username: name,
            user_email: email,
            otp: parseInt(otpToSend)
        };
        
        setFormData2(otpData);

        try {
            const response = await fetch(`${API_URL}/mailer/otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(otpData)
            });

            if (!response.ok) {
                Swal.fire({
                    text: "Email verification failed, check your network connection!",
                    icon: "error"
                });
                setIsLoading(false);
                throw new Error('Failed to verify email');
            }
            
            setShowAddModal(true);
            setOtpSent(true);
            setIsLoading(false);
            
            Swal.fire({
                text: `OTP sent successfully to ${email}! Please check your email.`,
                icon: "success",
                timer: 3000,
                showConfirmButton: false
            });

        } catch (err) {
            Swal.fire({
                text: "Email verification failed, check your network connection!",
                icon: "error"
            });
            setIsLoading(false);
        }
    };

    // Only generate OTP on initial mount if we don't have one
    useEffect(() => {
        const savedState = loadStateFromLocalStorage();
        if (!savedState || !savedState.originalOtp) {
            generateOtp();
        }
    }, []);
    
    useEffect(() => {
        setFormData2({
            username: name,
            user_email: email,
            otp: otp
        });
    }, [name, email, otp]);
    
    // Add event listener to restore modal state on page visibility change
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && showAddModal) {
                console.log('Page became visible, restoring teacher registration state...');
                // Page became visible again, ensure modal state is restored
                const savedState = loadStateFromLocalStorage();
                if (savedState) {
                    setName(savedState.name || '');
                    setSurname(savedState.surname || '');
                    setEmail(savedState.email || '');
                    setPhone(savedState.phone || '');
                    setCollegeName(savedState.collegeName || '');
                    setCollegeDescription(savedState.collegeDescription || '');
                    setPassword(savedState.password || '');
                    setConfirmPassword(savedState.confirmPassword || '');
                    setOtp(savedState.otp || '');
                    setOriginalOtp(savedState.originalOtp || savedState.otp || '');
                    setShowAddModal(savedState.showAddModal || false);
                    setOtpSent(savedState.otpSent || false);
                    
                    // Show expiry warning if needed
                    const timeRemaining = (savedState.timestamp + (30 * 60 * 1000)) - Date.now();
                    if (timeRemaining > 0 && timeRemaining < 5 * 60 * 1000) {
                        Swal.fire({
                            text: "Your registration session will expire in 5 minutes. Please complete the process soon.",
                            icon: "warning",
                            timer: 5000,
                            showConfirmButton: true
                        });
                    }
                } else if (showAddModal) {
                    // Session expired, close modal
                    Swal.fire({
                        text: "Your session has expired. Please start the registration process again.",
                        icon: "warning"
                    });
                    setShowAddModal(false);
                    localStorage.removeItem('teacherRegState');
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        // Set up an interval to check for expiry while modal is open
        let expiryInterval;
        if (showAddModal) {
            expiryInterval = setInterval(() => {
                const savedState = loadStateFromLocalStorage();
                if (!savedState && showAddModal) {
                    // Session expired
                    Swal.fire({
                        text: "Your session has expired. Please start the registration process again.",
                        icon: "warning"
                    });
                    setShowAddModal(false);
                    localStorage.removeItem('teacherRegState');
                }
            }, 60000); // Check every minute
        }
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (expiryInterval) {
                clearInterval(expiryInterval);
            }
        };
    }, [showAddModal]);

    document.addEventListener(
        "contextmenu", function(e)
        {
            e.preventDefault();
        }, false
    )

    return (
        <html lang="en">
            <body id="page-top">
                <div style={{ backgroundColor: 'rgb(246, 243, 237)', margin: 0, minHeight: '100vh' }}>
                    <div className="container" style={{ height: '100%' }}>
                        <div className="row justify-content-center" style={{ height: '100%', alignItems: 'center' }}>
                            <div className="col-xl-10 col-lg-12 col-md-9">
                                <div className="card o-hidden border-0 shadow-lg my-5">
                                    <div className="card-body p-0">
                                        <div className="row">
                                            <div className="col-lg-12">
                                                <div className="p-5">
                                                    <div className="sidebar-brand-icon rotate-n-0 text-center">
                                                        <img src="../tantak_logo.png" height="80px" width="80px" alt="Background" style={{ borderRadius: '50%' }} />
                                                    </div>
                                                    <h1 className="h5 text-gray-900 mb-4 text-center">Register Your College</h1>
                                                    <p className="text-center">Please fill in the details below to register your college.</p>

                                                    <form className="user" onSubmit={sendOtp}>
                                                        <div className="form-group">
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-user"
                                                                placeholder="Name"
                                                                value={name}
                                                                onChange={(e) => setName(e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-user"
                                                                placeholder="Surname"
                                                                value={surname}
                                                                onChange={(e) => setSurname(e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <input
                                                                type="email"
                                                                className="form-control form-control-user"
                                                                placeholder="Email"
                                                                value={email}
                                                                onChange={(e) => setEmail(e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <input
                                                                type="tel"
                                                                className="form-control form-control-user"
                                                                placeholder="Phone"
                                                                value={phone}
                                                                onChange={(e) => setPhone(e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-user"
                                                                placeholder="College Name"
                                                                value={collegeName}
                                                                onChange={(e) => setCollegeName(e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <textarea
                                                                className="form-control"
                                                                placeholder="Description of the College"
                                                                value={collegeDescription}
                                                                onChange={(e) => setCollegeDescription(e.target.value)}
                                                                required
                                                                rows="3"
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <input
                                                                type="file"
                                                                className="form-control"
                                                                accept="image/*"
                                                                onChange={(e) => setCollegeLogo(e.target.files[0])}
                                                                required
                                                            />
                                                            <small className="form-text text-muted">Upload a logo or image for your college profile.</small>
                                                        </div>
                                                        <hr></hr>
                                                        <div className="form-group">
                                                            <input
                                                                type="password"
                                                                className="form-control"
                                                                placeholder="Enter Password"
                                                                value={password}
                                                                onChange={(e) => setPassword(e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <input
                                                                type="password"
                                                                className="form-control"
                                                                placeholder="Confirm Password"
                                                                value={confirmPassword}
                                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                                required
                                                            />
                                                        </div>

                                                        <button
                                                            className="btn btn-dark btn-user btn-block"
                                                            type="submit"
                                                            style={{ backgroundColor: 'rgba(32, 70, 161)', borderColor: 'rgba(32, 70, 161)' }}
                                                            disabled={isLoading}
                                                        >
                                                            {isLoading ? "Sending OTP..." : "Register"}
                                                        </button>
                                                        <div className="text-center">
                                                            <p></p>
                                                            <a className="small" style={{ color: 'black' }} href="/">Already have an Account? Login!</a>
                                                        </div>
                                                    </form>
                                                    <hr />
                                                </div>
                                            </div>
                                            {isLoading && (
                                                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '-58px' }}>
                                                    <BarLoader size={40} width={'100%'} color="blue" loading />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* OTP Verification Modal */}
                {showAddModal && (
                    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => {
                        setShowAddModal(false);
                        localStorage.removeItem('teacherRegState');
                    }}>
                        <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-content" style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)', borderRadius: '8px' }}>
                                <div className="modal-header">
                                    <h5 className="modal-title">Email Verification</h5>
                                    <button type="button" className="close" onClick={() => {
                                        setShowAddModal(false);
                                        localStorage.removeItem('teacherRegState');
                                    }}>&times;</button>
                                </div>
                                <form onSubmit={validateOtp}>
                                    <div className="modal-body">
                                        <div className="form-group">
                                            <label className="modal-label">Enter OTP sent to your email ({email}):</label>
                                            <input 
                                                type="text" 
                                                className="form-control" 
                                                value={vOtp} 
                                                onChange={(e) => setVOtp(e.target.value)}
                                                required
                                                autoFocus
                                                placeholder="Enter 6-digit OTP"
                                                maxLength="6"
                                            />
                                            <small className="form-text text-muted">
                                                Please enter the 6-digit code sent to your email (valid for 30 minutes)
                                            </small>
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button type="button" className="btn btn-secondary" onClick={() => {
                                            setShowAddModal(false);
                                            localStorage.removeItem('teacherRegState');
                                        }}>Close</button>
                                        {isLoading && (
                                            <div style={{ marginTop: '8px', textAlign: 'center' }}>
                                                <div className="btn btn-primary" style={{ width: '5rem' }}>
                                                    <ClipLoader loading={isLoading} size={27} color="white" />
                                                </div>
                                            </div>
                                        )}
                                        {!isLoading && (
                                            <button type="submit" className="btn btn-primary">Verify OTP</button>
                                        )}
                                    </div>
                                    {isLoading && (
                                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '5px' }}>
                                            <BarLoader size={40} width={'100%'} color="blue" loading />
                                        </div>
                                    )}
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </body>
        </html>
    );
};

export default TeacherReg;

// import React, { useEffect, useState } from "react";
// import { useNavigate } from 'react-router-dom';
// import { API_URL } from "../../config";
// import Swal from "sweetalert2";
// import { BarLoader, ClipLoader } from "react-spinners";

// const TeacherReg = () => {
//     const navigate = useNavigate();
//     const [name, setName] = useState('');
//     const [surname, setSurname] = useState('');
//     const [email, setEmail] = useState('');
//     const [phone, setPhone] = useState('');
//     const [collegeName, setCollegeName] = useState('');
//     const [collegeDescription, setCollegeDescription] = useState('');
//     const [password, setPassword] = useState('');
//     const [otp, setOtp] = useState('');
//     const [vOtp, setVOtp] = useState('');
//     const [confirmPassword, setConfirmPassword] = useState('');
//     const [collegeLogo, setCollegeLogo] = useState(null);
//     const [showAddModal, setShowAddModal] = useState(false);
//     const [isLoading, setIsLoading] = useState(false);
//     const [formData2, setFormData2] = useState({
//         username: '',
//         user_email: '',
//         otp: ''
//     })

//     const handleRegister = async () => {
//         setIsLoading(true);

//         const formData = new FormData();
//         formData.append('name', name);
//         formData.append('surname', surname);
//         formData.append('email', email);
//         formData.append('phone', phone);
//         formData.append('password', password);
//         formData.append('collegeName', collegeName);
//         formData.append('collegeDescription', collegeDescription);
//         formData.append('file', collegeLogo);

//         try {
//             const response = await fetch(`${API_URL}/onboarding/teacher`, {
//                 method: "POST",
//                 body: formData
//             });

//             if (!response.ok) {
//                 throw new Error('Failed to register teacher');
//             }

//             const data = await response.json();
//             console.log(data.status)
//             if (data.status === '200') {
//                 // Alert user on success 
//                 Swal.fire({
//                     text: "Successfully registered! You can proceed to login",
//                     icon: "success"
//                 });

//                 setIsLoading(false);
//                 navigate('/');
//             } else if (data.status === '401') {
//                 Swal.fire({
//                     text: "The college name is already taken. Your college name should be unique!",
//                     icon: "error"
//                 });

//                 setIsLoading(false);
//             } else {
//                 Swal.fire({
//                     text: "This email is already registered with another college!",
//                     icon: "error"
//                 });

//                 setIsLoading(false);
//             }

//         } catch (err) {
//             Swal.fire({
//                 text: "Registration failed, check your network connection!",
//                 icon: "error"
//             });
//             setIsLoading(false);
//         }
//     };

//     const generateOtp = () => {

//         const min = 123456;
//         const max = 987987;
//         const random = min + (Math.floor(Math.random() * (max - min + 1)));
//         console.log('random number: ', random);
//         setOtp(random.toString());

//         setFormData2({ ...formData2, otp: random })
//     }

//     const validateOtp = (e) => {
//         e.preventDefault();
//         if (otp === vOtp) {
//             handleRegister();
//         } else {
//             console.log(otp)
//             console.log(vOtp)
//             Swal.fire({
//                 text: "Incorrect OTP",
//                 icon: "error"
//             });
//         }
//     }

//     const sendOtp = async (e) => {
//         e.preventDefault();
//         setIsLoading(true);
//         // console.log(formData2);

//         try {
//             const response = await fetch(`${API_URL}/mailer/otp`, {
//                 method: "POST",
//                 headers: { "Content-Type": "application/json" },
//                 body: JSON.stringify(formData2)
//             });

//             if (!response.ok) {
//                 Swal.fire({
//                     text: "Email verification failed, check your network connection!",
//                     icon: "error"
//                 });
//                 setIsLoading(false);
//                 throw new Error('Failed to verify email');
//             }
//             setShowAddModal(true);
//             setIsLoading(false);

//         } catch (err) {
//             Swal.fire({
//                 text: "Email verification failed, check your network connection!",
//                 icon: "error"
//             });
//             setIsLoading(false);
//         }
//     }

//     useEffect(() => {
//         generateOtp();
//     }, [])
//     useEffect(() => {
//         setFormData2({
//             username: name,
//             user_email: email,
//             otp: otp
//         })
//     }, [name, email])

//     document.addEventListener(
//         "contextmenu", function(e)
//         {
//             e.preventDefault();
//         }, false
//     )

//     return (
//         <html lang="en">

//             <body id="page-top">
//                 <div style={{ backgroundColor: 'rgb(246, 243, 237)', margin: 0 }}>
//                     <div className="container" style={{ height: '100%' }}>
//                         <div className="row justify-content-center" style={{ height: '100%', alignItems: 'center' }}>
//                             <div className="col-xl-10 col-lg-12 col-md-9">
//                                 <div className="card o-hidden border-0 shadow-lg my-5">
//                                     <div className="card-body p-0">
//                                         <div className="row">
//                                             <div className="col-lg-12">
//                                                 <div className="p-5">
//                                                     <div className="sidebar-brand-icon rotate-n-0">
//                                                         <img src="../tantak_logo.png" height="80px" width="80px" alt="Background" style={{ borderRadius: '50%' }} />
//                                                     </div>
//                                                     <h1 className="h5 text-gray-900 mb-4 text-center">Register Your College</h1>
//                                                     <p className="text-center">Please fill in the details below to register your college.</p>

//                                                     <form className="user" onSubmit={sendOtp}>
//                                                     {/* <form className="user" > */}
//                                                         <div className="form-group">
//                                                             <input
//                                                                 type="text"
//                                                                 className="form-control form-control-user"
//                                                                 placeholder="Name"
//                                                                 value={name}
//                                                                 onChange={(e) => setName(e.target.value)}
//                                                                 required
//                                                             />
//                                                         </div>
//                                                         <div className="form-group">
//                                                             <input
//                                                                 type="text"
//                                                                 className="form-control form-control-user"
//                                                                 placeholder="Surname"
//                                                                 value={surname}
//                                                                 onChange={(e) => setSurname(e.target.value)}
//                                                                 required
//                                                             />
//                                                         </div>
//                                                         <div className="form-group">
//                                                             <input
//                                                                 type="email"
//                                                                 className="form-control form-control-user"
//                                                                 placeholder="Email"
//                                                                 value={email}
//                                                                 onChange={(e) => setEmail(e.target.value)}
//                                                                 required
//                                                             />
//                                                         </div>
//                                                         <div className="form-group">
//                                                             <input
//                                                                 type="text"
//                                                                 className="form-control form-control-user"
//                                                                 placeholder="Phone"
//                                                                 value={phone}
//                                                                 onChange={(e) => setPhone(e.target.value)}
//                                                                 required
//                                                             />
//                                                         </div>
//                                                         <div className="form-group">
//                                                             <input
//                                                                 type="text"
//                                                                 className="form-control form-control-user"
//                                                                 placeholder="College Name"
//                                                                 value={collegeName}
//                                                                 onChange={(e) => setCollegeName(e.target.value)}
//                                                                 required
//                                                             />
//                                                         </div>
//                                                         <div className="form-group">
//                                                             <textarea
//                                                                 className="form-control"
//                                                                 placeholder="Description of the College"
//                                                                 value={collegeDescription}
//                                                                 onChange={(e) => setCollegeDescription(e.target.value)}
//                                                                 required
//                                                             />
//                                                         </div>
//                                                         <div className="form-group">
//                                                             <input
//                                                                 type="file"
//                                                                 className="form-control"
//                                                                 accept="image/*"
//                                                                 onChange={(e) => setCollegeLogo(e.target.files[0])}
//                                                                 required
//                                                             />
//                                                             <small className="form-text text-muted">Upload a logo or image for your college profile.</small>
//                                                         </div>
//                                                         <hr></hr>
//                                                         <div className="form-group">
//                                                             <input
//                                                                 type="password"
//                                                                 className="form-control"
//                                                                 placeholder="Enter Password"
//                                                                 value={password}
//                                                                 onChange={(e) => setPassword(e.target.value)}
//                                                                 required
//                                                             />
//                                                         </div>
//                                                         <div className="form-group">
//                                                             <input
//                                                                 type="password"
//                                                                 className="form-control"
//                                                                 placeholder="Confirm Password"
//                                                                 value={confirmPassword}
//                                                                 onChange={(e) => setConfirmPassword(e.target.value)}
//                                                                 required
//                                                             />
//                                                         </div>

//                                                         <button
//                                                             className="btn btn-dark btn-user btn-block"
//                                                             type="submit"
//                                                             style={{ backgroundColor: 'rgba(32, 70, 161)', borderColor: 'rgba(32, 70, 161)' }}
//                                                             disabled={isLoading}
//                                                         >
//                                                             Register
//                                                         </button>
//                                                         <div className="text-center">
//                                                             <p></p>
//                                                             <a className="small" style={{ color: 'black' }} href="/">Already have an Account? Login!</a>
//                                                         </div>
//                                                     </form>
//                                                     <hr />
//                                                 </div>
//                                             </div>
//                                             {isLoading && (
//                                                 <div style={{ display: 'flex', justifyContent: 'center', marginTop: '-58px' }}>
//                                                     <BarLoader size={40} width={'100%'} color="blue" loading />
//                                                 </div>
//                                             )}
//                                         </div>
//                                     </div>
//                                 </div>
//                             </div>
//                         </div>
//                     </div>
//                 </div>

//                 {/* Submit Assignment Modal */}
//                 {showAddModal && (
//                     <div className="modal fade show" style={{ display: 'block' }} onClick={() => setShowAddModal(false)}>
//                         <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
//                             <div className="modal-content" style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)', borderRadius: '8px' }}>
//                                 <div className="modal-header">
//                                     <h5 className="modal-title">Email verification</h5>
//                                     <button type="button" className="close" onClick={() => setShowAddModal(false)}>&times;</button>
//                                 </div>
//                                 <form onSubmit={validateOtp}>
//                                     <div className="modal-body">
//                                         <div className="form-group">
//                                             <label className="modal-label">Enter OTP sent to your email ({email}):</label>
//                                             <input type="number" className="form-control" value={vOtp} onChange={(e) => setVOtp(e.target.value)} />
//                                         </div>
//                                     </div>
//                                     <div className="modal-footer">
//                                         <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Close</button>
//                                         {isLoading && (
//                                             <div style={{ marginTop: '8px', textAlign: 'center' }}>
//                                                 <div className="btn btn-primary" style={{ width: '5rem' }}>
//                                                     <ClipLoader loading={isLoading} size={27} color="white" />
//                                                 </div>
//                                             </div>
//                                         )}
//                                         {!isLoading && (
//                                             <button type="submit" className="btn btn-primary">Submit</button>
//                                         )}
//                                     </div>
//                                     {isLoading && (
//                                         <div style={{ display: 'flex', justifyContent: 'center', marginTop: '5px' }}>
//                                             <BarLoader size={40} width={'100%'} color="blue" loading />
//                                         </div>
//                                     )}
//                                 </form>
//                             </div>
//                         </div>
//                     </div>
//                 )}
//             </body>

//         </html >
//     );
// };

// export default TeacherReg;

// import React, { useEffect, useState } from "react";
// import { useNavigate } from 'react-router-dom';
// import { API_URL } from "../../config";
// import Swal from "sweetalert2";
// import { BarLoader, ClipLoader } from "react-spinners";

// const TeacherReg = () => {
//     const navigate = useNavigate();
//     const [name, setName] = useState('');
//     const [surname, setSurname] = useState('');
//     const [email, setEmail] = useState('');
//     const [phone, setPhone] = useState('');
//     const [collegeName, setCollegeName] = useState('');
//     const [collegeDescription, setCollegeDescription] = useState('');
//     const [password, setPassword] = useState('');
//     const [otp, setOtp] = useState('');
//     const [vOtp, setVOtp] = useState('');
//     const [confirmPassword, setConfirmPassword] = useState('');
//     const [collegeLogo, setCollegeLogo] = useState(null);
//     const [showAddModal, setShowAddModal] = useState(false);
//     const [isLoading, setIsLoading] = useState(false);
//     const [formData2, setFormData2] = useState({
//         username: '',
//         user_email: '',
//         otp: ''
//     });

//     const handleRegister = async () => {
//         setIsLoading(true);

//         const formData = new FormData();
//         formData.append('name', name);
//         formData.append('surname', surname);
//         formData.append('email', email);
//         formData.append('phone', phone);
//         formData.append('password', password);
//         formData.append('collegeName', collegeName);
//         formData.append('collegeDescription', collegeDescription);
//         formData.append('file', collegeLogo);

//         try {
//             const response = await fetch(`${API_URL}/onboarding/teacher`, {
//                 method: "POST",
//                 body: formData
//             });

//             if (!response.ok) {
//                 throw new Error('Failed to register teacher');
//             }

//             const data = await response.json();
//             if (data.status === '200') {
//                 Swal.fire({
//                     text: "Successfully registered! You can proceed to login",
//                     icon: "success"
//                 });

//                 setIsLoading(false);
//                 navigate('/');
//             } else if (data.status === '401') {
//                 Swal.fire({
//                     text: "The college name is already taken. Your college name should be unique!",
//                     icon: "error"
//                 });

//                 setIsLoading(false);
//             } else {
//                 Swal.fire({
//                     text: "This email is already registered with another college!",
//                     icon: "error"
//                 });

//                 setIsLoading(false);
//             }

//         } catch (err) {
//             Swal.fire({
//                 text: "Registration failed, check your network connection!",
//                 icon: "error"
//             });
//             setIsLoading(false);
//         }
//     };

//     const generateOtp = () => {
//         const min = 123456;
//         const max = 987987;
//         const random = min + (Math.floor(Math.random() * (max - min + 1)));
//         setOtp(random);
//         setFormData2({ ...formData2, otp: random });
//     };

//     const validateOtp = (e) => {
//         e.preventDefault();
//         if (otp == vOtp) {
//             handleRegister();
//         } else {
//             Swal.fire({
//                 text: "Incorrect OTP",
//                 icon: "error"
//             });
//         }
//     };

//     const sendOtp = async (e) => {
//         e.preventDefault();
//         try {
//             const response = await fetch(`${API_URL}/mailer/otp`, {
//                 method: "POST",
//                 headers: { "Content-Type": "application/json" },
//                 body: JSON.stringify(formData2)
//             });

//             if (!response.ok) {
//                 Swal.fire({
//                     text: "Registration failed, check your network connection!",
//                     icon: "error"
//                 });
//                 throw new Error('Failed to register teacher');
//             }
//             setShowAddModal(true);

//         } catch (err) {
//             Swal.fire({
//                 text: "Registration failed, check your network connection!",
//                 icon: "error"
//             });
//             setIsLoading(false);
//         }
//     };

//     useEffect(() => {
//         generateOtp();
//     }, []);

//     useEffect(() => {
//         setFormData2({
//             username: name,
//             user_email: email,
//             otp: otp
//         });
//     }, [name, email]);

//     return (
//         <div style={{ backgroundColor: 'rgb(246, 243, 237)', margin: 0, height: '100vh', overflow: 'hidden' }}>
//             <div className="container">
//                 <div className="row justify-content-center" style={{ alignItems: 'center', minHeight: '100vh' }}>
//                     <div className="col-xl-10 col-lg-12 col-md-9">
//                         <div className="card o-hidden border-0 shadow-lg my-5">
//                             <div className="card-body p-0" style={{ overflowY: 'scroll', maxHeight: '500px', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
//                                 <style>
//                                     {`
//                                         .card-body::-webkit-scrollbar {
//                                             display: none; /* Hide scrollbar for Chrome, Safari, and Opera */
//                                         }
//                                     `}
//                                 </style>
//                                 <div className="row">
//                                     <div className="col-lg-12">
//                                         <div className="p-5">
//                                             <div className="sidebar-brand-icon rotate-n-0">
//                                                 <img src="../tantak_logo.png" height="80px" width="80px" alt="Background" style={{ borderRadius: '50%' }} />
//                                             </div>
//                                             <h1 className="h5 text-gray-900 mb-4 text-center">Register Your College</h1>
//                                             <p className="text-center">Please fill in the details below to register your college.</p>

//                                             <form className="user" onSubmit={sendOtp}>
//                                                 <div className="form-group">
//                                                     <input
//                                                         type="text"
//                                                         className="form-control form-control-user"
//                                                         placeholder="Name"
//                                                         value={name}
//                                                         onChange={(e) => setName(e.target.value)}
//                                                         required
//                                                     />
//                                                 </div>
//                                                 <div className="form-group">
//                                                     <input
//                                                         type="text"
//                                                         className="form-control form-control-user"
//                                                         placeholder="Surname"
//                                                         value={surname}
//                                                         onChange={(e) => setSurname(e.target.value)}
//                                                         required
//                                                     />
//                                                 </div>
//                                                 <div className="form-group">
//                                                     <input
//                                                         type="email"
//                                                         className="form-control form-control-user"
//                                                         placeholder="Email"
//                                                         value={email}
//                                                         onChange={(e) => setEmail(e.target.value)}
//                                                         required
//                                                     />
//                                                 </div>
//                                                 <div className="form-group">
//                                                     <input
//                                                         type="text"
//                                                         className="form-control form-control-user"
//                                                         placeholder="Phone"
//                                                         value={phone}
//                                                         onChange={(e) => setPhone(e.target.value)}
//                                                         required
//                                                     />
//                                                 </div>
//                                                 <div className="form-group">
//                                                     <input
//                                                         type="text"
//                                                         className="form-control form-control-user"
//                                                         placeholder="College Name"
//                                                         value={collegeName}
//                                                         onChange={(e) => setCollegeName(e.target.value)}
//                                                         required
//                                                     />
//                                                 </div>
//                                                 <div className="form-group">
//                                                     <textarea
//                                                         className="form-control"
//                                                         placeholder="Description of the College"
//                                                         value={collegeDescription}
//                                                         onChange={(e) => setCollegeDescription(e.target.value)}
//                                                         required
//                                                     />
//                                                 </div>
//                                                 <div className="form-group">
//                                                     <input
//                                                         type="file"
//                                                         className="form-control"
//                                                         accept="image/*"
//                                                         onChange={(e) => setCollegeLogo(e.target.files[0])}
//                                                         required
//                                                     />
//                                                     <small className="form-text text-muted">Upload a logo or image for your college profile.</small>
//                                                 </div>
//                                                 <hr />
//                                                 <div className="form-group">
//                                                     <input
//                                                         type="password"
//                                                         className="form-control"
//                                                         placeholder="Enter Password"
//                                                         value={password}
//                                                         onChange={(e) => setPassword(e.target.value)}
//                                                         required
//                                                     />
//                                                 </div>
//                                                 <div className="form-group">
//                                                     <input
//                                                         type="password"
//                                                         className="form-control"
//                                                         placeholder="Confirm Password"
//                                                         value={confirmPassword}
//                                                         onChange={(e) => setConfirmPassword(e.target.value)}
//                                                         required
//                                                     />
//                                                 </div>

//                                                 <button
//                                                     className="btn btn-dark btn-user btn-block"
//                                                     type="submit"
//                                                     style={{ backgroundColor: 'rgba(32, 70, 161)', borderColor: 'rgba(32, 70, 161)' }}
//                                                     disabled={isLoading}
//                                                 >
//                                                     Register
//                                                 </button>
//                                                 <div className="text-center">
//                                                     <p></p>
//                                                     <a className="small" style={{ color: 'black' }} href="/">Already have an Account? Login!</a>
//                                                 </div>
//                                             </form>
//                                             <hr />
//                                         </div>
//                                     </div>
//                                     {isLoading && (
//                                         <div style={{ display: 'flex', justifyContent: 'center', marginTop: '-58px' }}>
//                                             <BarLoader size={40} width={'100%'} color="blue" loading />
//                                         </div>
//                                     )}
//                                 </div>
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//             </div>

//             {/* Submit Assignment Modal */}
//             {showAddModal && (
//                 <div className="modal fade show" style={{ display: 'block' }} onClick={() => setShowAddModal(false)}>
//                     <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
//                         <div className="modal-content" style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)', borderRadius: '8px' }}>
//                             <div className="modal-header">
//                                 <h5 className="modal-title">Email verification</h5>
//                                 <button type="button" className="close" onClick={() => setShowAddModal(false)}>&times;</button>
//                             </div>
//                             <form onSubmit={validateOtp}>
//                                 <div className="modal-body">
//                                     <div className="form-group">
//                                         <label className="modal-label">Enter OTP sent to your email {otp} ({email}):</label>
//                                         <input type="number" className="form-control" value={vOtp} onChange={(e) => setVOtp(e.target.value)} />
//                                     </div>
//                                 </div>
//                                 <div className="modal-footer">
//                                     <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Close</button>
//                                     {isLoading && (
//                                         <div style={{ marginTop: '8px', textAlign: 'center' }}>
//                                             <div className="btn btn-primary" style={{ width: '5rem' }}>
//                                                 <ClipLoader loading={isLoading} size={27} color="white" />
//                                             </div>
//                                         </div>
//                                     )}
//                                     {!isLoading && (
//                                         <button type="submit" className="btn btn-primary">Submit</button>
//                                     )}
//                                 </div>
//                                 {isLoading && (
//                                     <div style={{ display: 'flex', justifyContent: 'center', marginTop: '5px' }}>
//                                         <BarLoader size={40} width={'100%'} color="blue" loading />
//                                     </div>
//                                 )}
//                             </form>
//                         </div>
//                     </div>
//                 </div>
//             )}
//         </div>
//     );
// };

// export default TeacherReg;

